import { GridWidgetFilter } from './grid-widget-filter';
import { pdsI18n } from '../../../src/pds-i18n';
import { NumberField } from './number-field';
import { GridWidgetDataField } from './grid-widget-data-field';
import { RestArgument } from './rest-argument';

export class NumberFilter extends GridWidgetFilter {

  comparator = '=';

  //Values controlled by the UI
  //These are the numbers we are searching for.
  searchValueNum: number | null | undefined;
  searchValueNum2: number | null | undefined;

  //These values are the numbers multiplied by the numbers.
  convertedSearchValueNum: number | null | undefined;
  convertedSearchValueNum2: number | null | undefined;

  selectedUnitConversion: number | null | undefined;
  selectedUnitConversion2: number | null | undefined;

  constructor(dataField: GridWidgetDataField) {
    super(dataField);

    let unitsMap = (<NumberField>this.dataField).unitsMap;
    if (unitsMap != null) {
      this.selectedUnitConversion = unitsMap.getFirstValue();
      this.selectedUnitConversion2 = unitsMap.getFirstValue();
    }
  }

  getTagName(): string {
    return 'pds-gw-number-filter';
  }

  preFilterUpdate(): void {
    //Set convertedSearchValueNum
    if (this.searchValueNum == null) {
      this.convertedSearchValueNum = undefined;
    } else if (this.selectedUnitConversion == null) {
      this.convertedSearchValueNum = this.searchValueNum;
    } else {
      this.convertedSearchValueNum = this.searchValueNum * this.selectedUnitConversion;
    }

    //Set convertedSearchValueNum
    if (this.searchValueNum2 == null) {
      this.convertedSearchValueNum2 = undefined;
    } else if (this.selectedUnitConversion2 == null) {
      this.convertedSearchValueNum2 = this.searchValueNum2;
    } else {
      this.convertedSearchValueNum2 = this.searchValueNum2 * this.selectedUnitConversion2;
    }
  }

  isBlank(): boolean {
    if (this.comparator === 'range') {
      return (this.searchValueNum == null || isNaN(this.searchValueNum)) &&
        (this.searchValueNum2 == null || isNaN(this.searchValueNum2));
    }

    //Nothing there is blank, or if they put in something that isn't a number it will be blank.
    return this.searchValueNum == null || isNaN(this.searchValueNum);
  }

  internalPassesFilter(dataFromDataRow): boolean {
    //dataFromDataRow should already be using the correct units.
    let regexFromFilter;
    // if it's undefined or null then do not pass
    if (dataFromDataRow == null || typeof dataFromDataRow !== 'number') {
      return false;
    }

    switch (this.comparator) {
      case '=':
        return dataFromDataRow === this.convertedSearchValueNum;
      case '>':
        return this.convertedSearchValueNum == null || dataFromDataRow > this.convertedSearchValueNum;
      case '>=':
        return this.convertedSearchValueNum == null || dataFromDataRow >= this.convertedSearchValueNum;
      case '<':
        return this.convertedSearchValueNum == null || dataFromDataRow < this.convertedSearchValueNum;
      case '<=':
        return this.convertedSearchValueNum == null || dataFromDataRow <= this.convertedSearchValueNum;
      case '~':
        return String(dataFromDataRow).search(String(this.convertedSearchValueNum)) >= 0;
      case 'range':
        let val1 = (this.convertedSearchValueNum == null || isNaN(this.convertedSearchValueNum) || dataFromDataRow >= this.convertedSearchValueNum);
        let val2 = (this.convertedSearchValueNum2 == null || isNaN(this.convertedSearchValueNum2) || dataFromDataRow <= this.convertedSearchValueNum2);
        return val1 &&
          val2;
      default:
        throw 'The comparator on the number filter is not set or is unsupported: ' + this.comparator;
    }
  }

  setQuickSearchValue(text: string): boolean {
    this.searchValueNum = pdsI18n.deLocalizeNumber(text);
    this.comparator = '~';
    return this.searchValueNum != null;
  }

  getRestArguments(): Array<RestArgument> {
    if (this.isBlank()) {
      return [];
    }
    if (this.comparator === 'range') {
      let restArgs: Array<RestArgument> = [];

      if (this.convertedSearchValueNum != null && !isNaN(this.convertedSearchValueNum)) {
        restArgs.push(new RestArgument(this.dataField.id, '>=', this.convertedSearchValueNum));
      }

      if (this.convertedSearchValueNum2 != null && !isNaN(this.convertedSearchValueNum2)) {
        restArgs.push(new RestArgument(this.dataField.id, '<=', this.convertedSearchValueNum2));
      }

      return restArgs;
    } else {
      return [
        new RestArgument(this.dataField.id, this.comparator, this.convertedSearchValueNum)
      ];
    }
  }

  getTagText(): string {
    if (this.comparator === 'range') {
      return pdsI18n.localizeNumber(this.searchValueNum) + ' - ' + pdsI18n.localizeNumber(this.searchValueNum2);
    } else {
      return this.comparator + ' ' + pdsI18n.localizeNumber(this.searchValueNum);
    }
  }

  getPersistenceObject(): Object | undefined {
    let state = {
      comparator: this.comparator,
      searchValueNum: this.searchValueNum,
      searchValueNum2: this.searchValueNum2,
      selectedUnitConversion: this.selectedUnitConversion,
      selectedUnitConversion2: this.selectedUnitConversion2
    };
    return state;
  }

  restoreFromPersistenceObject(value: Object): void {
    let state = <any>value;
    this.comparator = state.comparator;
    this.searchValueNum = state.searchValueNum;
    this.searchValueNum2 = state.searchValueNum2;
    this.selectedUnitConversion = state.selectedUnitConversion;
    this.selectedUnitConversion2 = state.selectedUnitConversion2;
  }
}
